import { useLocation } from 'react-router';
import { useAIContext, aiValue } from './useAIContext';
import { useSkillsFeatureAccess } from '@/hooks/skills/useSkillsFeatureAccess';

/** Feature keys the user has access to, e.g. from /feature-access/check. */
type FeatureAccess = Record<string, boolean | undefined>;

interface SiteMapNode {
    _description?: string;
    _navigable?: boolean;
    /** Feature key the user must have access to for this node and its children to exist. */
    _featureAccess?: string;
    [key: string]: SiteMapNode | string | boolean | undefined;
}

const SITE_MAP: SiteMapNode = {
    'dashboard': {
        _description: 'Dashboard - the dashboard gives a high level overview of gozig to the user',
    },
    'profile': {
        _description: 'Profile - where the user can view and edit their personal profile information',
    },
    'explore': {
        '_description': 'Explore - where the user can browse and discover careers, programs, colleges, and opportunities',
        'careers': {
            _description: 'Explore Careers - browse and search careers. Supports ?search= to pre-filter results',
        },
        'programs': {
            '_description': 'Explore Programs - browse and search educational programs. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Program Details - viewing details about a specific educational program',
                _navigable: false,
            },
        },
        'institutions': {
            '_description': 'Explore Institutions - browse and search colleges and universities. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Institution Details - viewing details about a specific college or university',
                _navigable: false,
            },
        },
        'funding': {
            '_description': 'Explore Funding - browse and search financial aid, grants, and scholarships. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Funding Details - viewing details about a specific financial aid opportunity',
                _navigable: false,
            },
        },
        'certifications-and-licenses': {
            '_description': 'Explore Certifications and Licenses - browse and search professional certifications and licenses. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Certification or License Details - viewing details about a specific certification or license',
                _navigable: false,
            },
        },
        'apprenticeships': {
            '_description': 'Explore Apprenticeships - browse and search apprenticeship opportunities. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Apprenticeship Details - viewing details about a specific apprenticeship',
                _navigable: false,
            },
        },
        'internships': {
            '_description': 'Explore Internships - browse and search internship opportunities. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Internship Details - viewing details about a specific internship',
                _navigable: false,
            },
        },
        'jobs': {
            '_description': 'Explore Jobs - browse and search job listings. Supports ?search= to pre-filter results',
            ':id': {
                _description: 'Job Details - viewing details about a specific job listing',
                _navigable: false,
            },
        },
        'financial-aid': {
            ':id': {
                _description: 'Financial Aid Details - viewing details about a specific financial aid opportunity',
                _navigable: false,
            },
        },
    },
    'assessments': {
        _description: 'Assessments - where the user can take career assessments to discover their interests and strengths',
        results: {
            'career-types': {
                '_description': 'Career Types Results - viewing career type assessment results',
                ':id': {
                    _description: 'Career Type Details - viewing details about a specific career type result',
                    _navigable: false,
                },
            },
        },
    },
    'assessment': {
        ':id': {
            _description: 'Take Assessment - taking a career assessment',
            _navigable: false,
            result: {
                ':resultId': {
                    _description: 'Assessment Result - viewing results from a completed assessment',
                    _navigable: false,
                },
                'summary': {
                    _description: 'Assessment Summary - viewing a summary of assessment results',
                    _navigable: false,
                },
            },
        },
    },
    'skills': {
        '_description': 'Skills - where the user can view and manage their skills inventory',
        'add': {
            '_description': 'Add Skills - where the user can add skills to their skills inventory. From here they can upload a resume, CV, or transcript to have skills extracted automatically, or get skill suggestions based on the skills they already have',
            '_featureAccess': 'athena_skills_extraction',
            'upload-document': {
                _description: 'Upload Document - skill extraction: the user uploads a resume, CV, or transcript and we identify skills from it for them to review and add to their skills inventory',
            },
        },
        'suggested': {
            _description: 'Suggested Skills - skill expansion: suggestions for more skills the user could add, based on the skills already in their skills inventory',
            _featureAccess: 'athena_skills_extraction',
        },
        'review-inferred-skills': {
            _description: 'Review Extracted Skills - reviewing skills extracted from an uploaded document before adding them to the skills inventory',
            _navigable: false,
            _featureAccess: 'athena_skills_extraction',
        },
    },
    'journeys': {
        '_description': 'Journeys - where the user can view and manage their career journeys',
        'new': {
            _description: 'New Journey - creating a new career journey',
        },
        ':id': {
            _description: 'Journey Details - viewing details of a specific journey',
            _navigable: false,
        },
    },
    'careers': {
        ':id': {
            '_description': 'Career Details - viewing detailed information about a specific career',
            '_navigable': false,
            'qualifications': {
                _description: 'Career Qualifications - viewing qualifications needed for a specific career',
                _navigable: false,
            },
            'paths': {
                _description: 'Career Paths - viewing career progression paths',
                _navigable: false,
            },
            'programs': {
                _description: 'Career Programs - exploring educational programs for a specific career',
                _navigable: false,
            },
            'colleges': {
                '_description': 'Career Colleges - exploring colleges related to a specific career',
                '_navigable': false,
                ':collegeId': {
                    _description: 'College Details - viewing details about a specific college',
                    _navigable: false,
                },
            },
            'jobs': {
                _description: 'Career Jobs - exploring job listings for a specific career',
                _navigable: false,
            },
            'career-tree': {
                _description: 'Career Tree - viewing the career hierarchy and progression paths',
                _navigable: false,
            },
            'explore': {
                'programs': {
                    ':programId': {
                        _description: 'Career Program Details - viewing details about a program for this career',
                        _navigable: false,
                    },
                },
                'certifications-and-licenses': {
                    ':credentialId': {
                        _description: 'Career Credential Details - viewing details about a certification or license for this career',
                        _navigable: false,
                    },
                },
                'jobs': {
                    ':jobId': {
                        _description: 'Career Job Details - viewing details about a specific job for this career',
                        _navigable: false,
                    },
                },
                'apprenticeships': {
                    ':apprenticeshipId': {
                        _description: 'Career Apprenticeship Details - viewing details about an apprenticeship for this career',
                        _navigable: false,
                    },
                },
                'internships': {
                    ':jobId': {
                        _description: 'Career Internship Details - viewing details about an internship for this career',
                        _navigable: false,
                    },
                },
            },
        },
    },
    'surveys': {
        'career-confidence': {
            ':id': {
                _description: 'Career Confidence Check-In - viewing career confidence check-in results',
                _navigable: false,
                result: {
                    summary: {
                        _description: 'Career Confidence Summary - viewing a summary of career confidence results',
                        _navigable: false,
                    },
                },
            },
        },
    },
    'survey': {
        ':id': {
            _description: 'Take Survey - taking a career confidence survey',
            _navigable: false,
        },
    },
    'get-started': {
        _description: 'Get Started - the onboarding page for new users',
    },
    'privacy-policy': {
        _description: 'Privacy Policy - the privacy policy page',
    },
    'terms-of-use': {
        _description: 'Terms and Conditions - the terms and conditions page',
    },
    'contact-us': {
        _description: 'Contact Us - the support and help page',
    },
    'faqs': {
        _description: 'FAQs - frequently asked questions page',
    },
    'glossary': {
        _description: 'Glossary - definitions and terms used in the product',
    },
    'changelog': {
        '_description': 'Changelog - product updates from GoZig',
        ':slug': {
            _description: 'Changelog detail - one product update',
        },
    },
    'partners-and-vendors': {
        _description: 'Partners and Vendors - information about vendors and partners',
    },
    'login-error': {
        _description: 'Login Error - an error occurred during authentication',
        _navigable: false,
    },
    'change-password-migration': {
        _description: 'Change Password Migration - password migration flow',
        _navigable: false,
    },
    'link-accounts': {
        _description: 'Link Accounts - account linking flow',
        _navigable: false,
    },
    'check-your-email': {
        _description: 'Check Your Email - email verification step',
        _navigable: false,
    },
    'login': {
        _description: 'Login - authentication redirect',
        _navigable: false,
    },
    'logout': {
        _description: 'Logout - sign out redirect',
        _navigable: false,
    },
    'style-guide': {
        _description: 'Style Guide - developer style guide',
        _navigable: false,
    },
    'login-demo': {
        _description: 'Login Demo - demo authentication flow',
        _navigable: false,
    },
};

const hasNodeAccess = (node: SiteMapNode, featureAccess: FeatureAccess): boolean =>
    !node._featureAccess || featureAccess[node._featureAccess] === true;

const walkNavigablePages = (featureAccess: FeatureAccess): { path: string; description: string }[] => {
    const pages: { path: string; description: string }[] = [];

    const walk = (node: SiteMapNode, pathParts: string[]) => {
        for (const [key, value] of Object.entries(node)) {
            if (key.startsWith('_') || typeof value !== 'object' || !value) continue;

            const child = value as SiteMapNode;
            if (!hasNodeAccess(child, featureAccess)) continue;

            const currentParts = [...pathParts, key];

            if (child._description && child._navigable !== false) {
                pages.push({ path: `/${currentParts.join('/')}`, description: child._description });
            }

            walk(child, currentParts);
        }
    };

    walk(SITE_MAP, []);

    return pages;
};

export const getNavigablePages = (featureAccess: FeatureAccess = {}): string =>
    walkNavigablePages(featureAccess)
        .map(({ path, description }) => `${path} - ${description}`)
        .join('\n');

interface ResolvedRoute {
    node: SiteMapNode;
    description: string | null;
}

const resolvePath = (pathname: string, featureAccess: FeatureAccess): ResolvedRoute | null => {
    const pathSplit = pathname.split('/').slice(1);

    return pathSplit.reduce<ResolvedRoute | null>(
        (acc, pathPart) => {
            if (!acc) return null;

            let next = acc.node[pathPart];

            if (!next) {
                const colonKey = Object.keys(acc.node).find(k => k.startsWith(':'));
                next = colonKey ? acc.node[colonKey] : undefined;
            }

            if (!next || typeof next !== 'object') return null;

            const child = next as SiteMapNode;
            if (!hasNodeAccess(child, featureAccess)) return null;

            return {
                node: child,
                description: child._description ?? acc.description,
            };
        },
        { node: SITE_MAP, description: null },
    );
};

export const getPageDescription = (pathname: string, featureAccess: FeatureAccess = {}): string | null => {
    if (!pathname.startsWith('/')) {
        return 'Not a valid link';
    }

    return resolvePath(pathname, featureAccess)?.description ?? null;
};

export const isNavigableRoute = (pathname: string, featureAccess: FeatureAccess = {}): boolean => {
    if (!pathname.startsWith('/')) return false;

    const resolved = resolvePath(pathname, featureAccess);
    return resolved !== null && resolved.description !== null && resolved.node._navigable !== false;
};

/**
 * Feature access for every `_featureAccess` key used in the site map.
 */
export const useSiteMapFeatureAccess = (): FeatureAccess => {
    const { hasAccess: hasSkillsFeatureAccess } = useSkillsFeatureAccess();

    return { athena_skills_extraction: hasSkillsFeatureAccess === true };
};

/**
 * Hook that automatically adds the current page context for the AI.
 * This should be used at a high level in the app (e.g., Layout component).
 */
export const useAIPageContext = () => {
    const location = useLocation();
    const featureAccess = useSiteMapFeatureAccess();

    const currentPage = location.pathname;
    const pageDescription = getPageDescription(location.pathname, featureAccess);

    useAIContext({
        currentPage: aiValue(currentPage, pageDescription ?? ''),
    });

    useAIContext(
        {
            availablePages: aiValue(getNavigablePages(featureAccess), 'All available pages the user can navigate to'),
        },
        { persist: true },
    );
};
